import Dexie, { Table } from 'dexie';
import type { User } from '@demoops/shared-types';
import { clearApiCaches } from './apiCaching';
import { requestOutboxSync } from '../pwa/backgroundSync';

/** Offline reliability §1: a row in `syncing` is owned by whoever stamped
 *  `leaseUntil` (the open app or the service worker), for this long. The
 *  owner renews it after every replay step; anyone else leaves the row alone
 *  until it lapses. */
export const LEASE_MS = 60_000;

/** Rows drained per pass — the app's triggerSync and the worker's sync handler
 *  take the same bite so a phone never replays a whole backlog in one go. */
export const SYNC_BATCH_SIZE = 20;

// Session data stored locally
export interface LocalSession {
  id: 'current';
  token: string;
  user: User;
  expiresAt: string;
}

// Settings stored locally
export interface LocalSetting {
  key: string;
  value: unknown;
}

export type MutationStatus = 'pending' | 'syncing' | 'failed' | 'completed' | 'conflict';

// Mutation outbox for offline operations
export interface MutationEntry {
  id?: number;
  idempotencyKey: string;   // == offline_id sent to the server (server dedupes on this)
  type: string;             // 'create' | 'update' | ...
  entityType: string;       // replay-registry kind, e.g. 'assignment.create'
  entityId?: string;
  label?: string;           // human summary for the sync panel
  payload: unknown;
  createdAt: Date;
  attempts: number;
  lastAttempt?: Date;
  error?: string;
  conflicts?: unknown[];    // populated when the server reports a scheduling conflict
  progress?: unknown;       // resumable state for multi-step replays (e.g. form submit)
  status: MutationStatus;
  /** While `syncing`: the replaying context (app tab or service worker) holds
   *  this row until then — see LEASE_MS. Absent/expired means nobody does. */
  leaseUntil?: Date;
}

// Sync metadata for entities
export interface SyncMeta {
  entityType: string;
  lastSyncAt: Date;
  version: number;
}

// Cached entities (generic structure for caching API responses)
export interface CachedEntity {
  id: string;
  entityType: string;
  data: unknown;
  syncedAt: Date;
  version: number;
}

// Offline attachment blobs — decoupled from the mutation payload so they can be
// deleted independently when their outbox entry completes or is discarded.
export interface OfflineAttachment {
  id: string;          // uuid — referenced from the outbox payload as attachmentId
  outboxKey: string;   // == owning MutationEntry.idempotencyKey (for bulk cleanup)
  fieldName?: string;  // form field / daily-log slot this belongs to
  filename: string;
  mimeType: string;
  size: number;        // compressed byte size
  blob: Blob;          // compressed image (or original for non-images)
  thumbnail?: Blob;    // optional; unused for now (preview uses the compressed blob)
  createdAt: Date;
}

class DemoOpsDatabase extends Dexie {
  session!: Table<LocalSession, 'current'>;
  settings!: Table<LocalSetting, string>;
  mutations!: Table<MutationEntry, number>;
  syncMeta!: Table<SyncMeta, string>;
  cache!: Table<CachedEntity, string>;
  attachments!: Table<OfflineAttachment, string>;
  formDrafts!: Table<import('./formDrafts').FormDraft, string>;

  constructor() {
    super('demoops');

    this.version(1).stores({
      // Primary key, indexed fields
      session: 'id',
      settings: 'key',
      mutations: '++id, idempotencyKey, status, entityType, createdAt',
      syncMeta: 'entityType',
      cache: 'id, entityType, syncedAt',
    });

    this.version(2).stores({
      attachments: 'id, outboxKey',
    });

    // F-2 (E2E 2026-08-24): unsaved form work must survive a remount — a PWA
    // update reload mid-fill wiped a 42-field daily log. Drafts are device-
    // local, keyed per form surface, and cleared the moment work is durable.
    this.version(3).stores({
      formDrafts: 'key, updatedAt',
    });

    // Offline reliability §1 (2026-09-14): `leaseUntil` on mutation rows.
    // The field is unindexed (a lease is checked on rows already selected by
    // status), so the index list is unchanged — the bump records that rows
    // written from here on may carry a lease, and gives the service worker,
    // which opens this same database on its own, a schema to agree on.
    this.version(4).stores({
      mutations: '++id, idempotencyKey, status, entityType, createdAt',
    });
  }
}

export const db = new DemoOpsDatabase();

/**
 * Device state a feature keeps for itself, wiped with everything below
 * (Docuvault, 2026-10-10: the upload tray holds the files being sent and
 * their names). The feature registers its own clear-out when it loads,
 * because this module cannot import it: the service worker bundles db.ts
 * (swOutboxSync), and a feature's queue brings the API client and React in
 * with it. Nothing registers inside the service worker, so there the wipe is
 * exactly what it was. Returns the unregister function.
 */
const wipeHooks = new Set<() => void | Promise<void>>();

export function onDeviceWipe(hook: () => void | Promise<void>): () => void {
  wipeHooks.add(hook);
  return () => { wipeHooks.delete(hook); };
}

/** Wipe this device: every Dexie store (settings/device_id kept), the
 *  service worker's API caches — cached JSON is cookie-agnostic, so it must
 *  never survive an identity change (BUGS #21/#22) — and every feature's own
 *  device state (onDeviceWipe). A hook that fails never stops the wipe. */
export async function clearAllOfflineData(): Promise<void> {
  await Promise.all([
    db.session.clear(),
    db.mutations.clear(),
    db.syncMeta.clear(),
    db.cache.clear(),
    db.attachments.clear(),
    db.formDrafts.clear(),
    clearApiCaches(),
    ...[...wipeHooks].map((hook) => Promise.resolve().then(hook).catch(() => undefined)),
    // Keep settings (like device_id)
  ]);
}

// Helper to get pending mutations count
export async function getPendingMutationsCount(): Promise<number> {
  return db.mutations.where('status').equals('pending').count();
}

// Enqueue a mutation using a caller-supplied idempotency key (the offline_id that
// also goes to the server). Using the SAME key for the direct attempt and the
// queued replay is what makes retries safe (server dedupes on offline_id).
export async function enqueueMutation(entry: {
  entityType: string;
  type?: string;
  payload: unknown;
  idempotencyKey: string;
  entityId?: string;
  label?: string;
  progress?: unknown;    // seed resumable state (e.g. a known logId so replay skips the upsert)
}): Promise<number> {
  const id = await db.mutations.add({
    idempotencyKey: entry.idempotencyKey,
    type: entry.type ?? 'create',
    entityType: entry.entityType,
    entityId: entry.entityId,
    label: entry.label,
    payload: entry.payload,
    progress: entry.progress,
    createdAt: new Date(),
    attempts: 0,
    status: 'pending',
  });
  // Ask the browser to wake the service worker once it has a network, so the
  // row still sends after this tab is gone (Chromium/Android; a no-op
  // elsewhere). Fire-and-forget: the row is durable already.
  void requestOutboxSync();
  return id;
}

/** Is this row's lease still in the future? Tolerates a lease stored as a
 *  string by an older structured-clone path. */
export function leaseIsLive(m: Pick<MutationEntry, 'leaseUntil'>, now: Date = new Date()): boolean {
  if (!m.leaseUntil) return false;
  const until = m.leaseUntil instanceof Date ? m.leaseUntil : new Date(m.leaseUntil);
  return !Number.isNaN(until.getTime()) && until.getTime() > now.getTime();
}

/**
 * Take a pending row for replay: `syncing`, one more attempt, a fresh lease.
 * Atomic in one transaction, so two contexts (an open tab and the service
 * worker, or two tabs) cannot both replay the same row — the loser gets
 * `null` and moves on. Returns the row as it now stands.
 */
export async function claimMutation(id: number, now: Date = new Date()): Promise<MutationEntry | null> {
  return db.transaction('rw', db.mutations, async () => {
    const m = await db.mutations.get(id);
    if (!m || m.status !== 'pending') return null;
    const changes = {
      status: 'syncing' as const,
      lastAttempt: now,
      attempts: m.attempts + 1,
      leaseUntil: new Date(now.getTime() + LEASE_MS),
    };
    await db.mutations.update(id, changes);
    return { ...m, ...changes };
  });
}

export async function getMutationsByStatuses(statuses: MutationStatus[]): Promise<MutationEntry[]> {
  return db.mutations.where('status').anyOf(statuses).sortBy('createdAt');
}

/** Count of items needing user attention (won't self-resolve). */
export async function getAttentionCount(): Promise<number> {
  return db.mutations.where('status').anyOf(['failed', 'conflict']).count();
}

/** Everything a logout would destroy: queued/failed work + staged photo blobs. */
export interface UnsyncedSummary { items: number; blobs: number }

export async function getUnsyncedSummary(): Promise<UnsyncedSummary> {
  const items = await db.mutations.where('status').anyOf(['pending', 'syncing', 'failed', 'conflict']).count();
  const blobs = await db.attachments.count();
  return { items, blobs };
}

/** One honest last try before deletion: failed items back to pending.
 *  Conflicts are NOT touched — force is a deliberate per-item choice. */
export async function requeueFailedMutations(): Promise<void> {
  await db.mutations.where('status').equals('failed')
    .modify({ status: 'pending', attempts: 0, error: undefined });
}

/** BUGS #23: rows stuck in 'syncing' after a crash/kill mid-replay would
 *  never retry (triggerSync drains 'pending' only). Reset — re-queueing is
 *  safe because the server dedupes on offline_id. A row whose lease is still
 *  live is NOT stale: another context (the service worker, another tab) is
 *  replaying it right now and will settle it, or the lease lapses within
 *  LEASE_MS and the next pass picks it up. */
export async function recoverStaleSyncing(now: Date = new Date()): Promise<number> {
  return db.mutations.where('status').equals('syncing')
    .and((m) => !leaseIsLive(m, now))
    .modify({ status: 'pending', leaseUntil: undefined });
}

/** BUGS #24: completed rows are never shown and were never deleted —
 *  unbounded IndexedDB growth on field devices. Boot-time prune. */
export async function pruneCompletedMutations(maxAgeDays = 30): Promise<number> {
  const cutoff = new Date(Date.now() - maxAgeDays * 24 * 60 * 60 * 1000);
  return db.mutations.where('status').equals('completed')
    .and((m) => m.createdAt < cutoff)
    .delete();
}
